import React, { useState, useEffect } from 'react';
import { Shield, AlertCircle, Info, ChevronDown } from 'lucide-react';
import api from '../utils/api';

/**
 * Knowledge Access Settings
 * 
 * Allows admins to configure user permissions for knowledge graph chat queries.
 * Controls what data users can access through the AI chat interface.
 */
const KnowledgeAccessSettings = ({ user, currentOrganization }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  
  const userRole = user?.role || 'viewer';
  const isAdmin = ['owner', 'admin'].includes(userRole);

  // Permission options
  const clientScopes = [
    { value: 'all_clients', label: 'All Clients', description: 'Can query all clients in the workspace' },
    { value: 'team_clients', label: 'Team Clients', description: 'Can query clients assigned to their team' },
    { value: 'assigned_only', label: 'Assigned Only', description: 'Can only query clients directly assigned to them' },
    { value: 'own_records_only', label: 'Own Records Only', description: 'Can only query their own personal records' }
  ];

  const sensitivityLevels = [
    { value: 'public', label: 'Public', description: 'Can only access public information' },
    { value: 'internal', label: 'Internal', description: 'Can access internal team information' },
    { value: 'confidential', label: 'Confidential', description: 'Can access confidential business data' },
    { value: 'restricted', label: 'Restricted', description: 'Full access to all sensitivity levels' }
  ];

  const aiActionLevels = [
    { value: 'read_only', label: 'Read Only', description: 'Can only view and retrieve information' },
    { value: 'read_suggest', label: 'Read + Suggest', description: 'Can view info and suggest actions (requires confirmation)' },
    { value: 'read_write', label: 'Full Access', description: 'Can view, suggest, and execute actions' }
  ];

  const dataCategories = [
    { key: 'access_financial', label: 'Financial', description: 'Invoices, payments, budgets' },
    { key: 'access_communications', label: 'Communications', description: 'Emails, messages, calls' },
    { key: 'access_documents', label: 'Documents', description: 'Files, contracts, proposals' },
    { key: 'access_meetings', label: 'Meetings', description: 'Meeting notes, transcripts' },
    { key: 'access_tasks', label: 'Tasks', description: 'Tasks, projects, deliverables' },
    { key: 'access_contacts', label: 'Contacts', description: 'Client contacts and profiles' },
    { key: 'access_internal_notes', label: 'Internal Notes', description: 'Private team notes and comments' }
  ];

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/team/members');
      setUsers(response.data.members || []);
      
      // Auto-select current user for non-admins
      if (!isAdmin && user) {
        setSelectedUser(user.sub);
        await fetchUserPermissions(user.sub);
      }
    } catch (err) {
      console.error('Error fetching team members:', err);
      setError('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPermissions = async (userId) => {
    try {
      setLoading(true);
      const response = await api.get(`/settings/knowledge-permissions/${userId}`);
      setPermissions(response.data.permissions);
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setError('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = async (userId) => {
    setSelectedUser(userId);
    setError(null);
    setSuccess(null);
    await fetchUserPermissions(userId);
  };

  const handleSave = async () => {
    if (!permissions || !selectedUser) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await api.put(`/settings/knowledge-permissions/${selectedUser}`, permissions);
      
      setSuccess('Permissions updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating permissions:', err);
      setError(err.response?.data?.detail || 'Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selectedUser) return;
    if (!window.confirm('Reset permissions to role-based defaults?')) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await api.post(`/settings/knowledge-permissions/${selectedUser}/reset`);
      await fetchUserPermissions(selectedUser);
      
      setSuccess('Permissions reset to defaults');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error resetting permissions:', err);
      setError(err.response?.data?.detail || 'Failed to reset permissions');
    } finally {
      setSaving(false);
    }
  };

  const updatePermission = (field, value) => {
    setPermissions(prev => ({ ...prev, [field]: value }));
  };

  const getAccessSummary = () => {
    if (!permissions) return '';
    
    const scope = clientScopes.find(s => s.value === permissions.client_scope)?.label || 'Unknown';
    const ceiling = sensitivityLevels.find(s => s.value === permissions.sensitivity_ceiling)?.label || 'Unknown';
    const action = aiActionLevels.find(a => a.value === permissions.ai_action_level)?.label || 'Unknown';
    
    const allowedCategories = dataCategories.filter(cat => permissions[cat.key]).map(c => c.label);
    const categoryList = allowedCategories.length > 0 ? allowedCategories.join(', ') : 'None';
    
    const temporal = permissions.temporal_access === 'recent_only' 
      ? `last ${permissions.temporal_months} months` 
      : 'all time';

    return `This user can query ${scope.toLowerCase()}, including ${categoryList} data, up to ${ceiling.toLowerCase()} level, with ${action.toLowerCase()} permissions, for ${temporal}.`;
  };

  if (!isAdmin) {
    return (
      <div className="p-6 max-w-4xl">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-6 h-6 text-blue-600" />
          <h2 className="text-2xl font-bold">Your Knowledge Access</h2>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-700">
            <Info className="w-4 h-4 inline mr-2" />
            You can view your own knowledge access permissions here. Contact an admin to request changes.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading your permissions...</div>
        ) : permissions ? (
          <div className="space-y-6">
            <PermissionsDisplay permissions={permissions} dataCategories={dataCategories} />
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">No permissions configured</div>
        )}
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-blue-600" />
          <h2 className="text-2xl font-bold">Knowledge Access Permissions</h2>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-blue-700">
          <Info className="w-4 h-4 inline mr-2" />
          Control what data users can access through the AI knowledge graph chat. 
          These permissions apply to all AI-powered queries and limit what information the system retrieves.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-700">
            <AlertCircle className="w-4 h-4 inline mr-2" />
            {error}
          </p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      <div className="grid grid-cols-3 gap-6">
        {/* User List */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="font-semibold mb-4">Team Members</h3>
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {users.map(member => (
              <button
                key={member.id}
                onClick={() => handleUserSelect(member.id)}
                className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                  selectedUser === member.id
                    ? 'bg-blue-100 border border-blue-300'
                    : 'hover:bg-gray-100'
                }`}
              >
                <div className="font-medium text-sm">{member.name}</div>
                <div className="text-xs text-gray-500">{member.role}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Permissions Editor */}
        <div className="col-span-2 bg-white border border-gray-200 rounded-lg p-6">
          {!selectedUser ? (
            <div className="text-center py-16 text-gray-500">
              Select a user to view and edit their permissions
            </div>
          ) : loading ? (
            <div className="text-center py-16 text-gray-500">Loading permissions...</div>
          ) : permissions ? (
            <div className="space-y-6">
              {/* Client Scope */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client Access Scope
                </label>
                <select
                  value={permissions.client_scope}
                  onChange={(e) => updatePermission('client_scope', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {clientScopes.map(scope => (
                    <option key={scope.value} value={scope.value}>{scope.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {clientScopes.find(s => s.value === permissions.client_scope)?.description}
                </p>
              </div>

              {/* Data Categories */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data Categories
                </label>
                <div className="space-y-2">
                  {dataCategories.map(category => (
                    <label key={category.key} className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={permissions[category.key]}
                        onChange={(e) => updatePermission(category.key, e.target.checked)}
                        className="mt-1"
                      />
                      <div>
                        <div className="text-sm font-medium">{category.label}</div>
                        <div className="text-xs text-gray-500">{category.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Sensitivity Ceiling */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sensitivity Level
                </label>
                <select
                  value={permissions.sensitivity_ceiling}
                  onChange={(e) => updatePermission('sensitivity_ceiling', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {sensitivityLevels.map(level => (
                    <option key={level.value} value={level.value}>{level.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {sensitivityLevels.find(l => l.value === permissions.sensitivity_ceiling)?.description}
                </p>
              </div>

              {/* AI Action Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  AI Actions
                </label>
                <div className="space-y-2">
                  {aiActionLevels.map(level => (
                    <label key={level.value} className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="ai_action_level"
                        checked={permissions.ai_action_level === level.value}
                        onChange={() => updatePermission('ai_action_level', level.value)}
                        className="mt-1"
                      />
                      <div>
                        <div className="text-sm font-medium">{level.label}</div>
                        <div className="text-xs text-gray-500">{level.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Temporal Access */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Time Range
                </label>
                <div className="space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={permissions.temporal_access === 'all_time'}
                      onChange={() => {
                        updatePermission('temporal_access', 'all_time');
                        updatePermission('temporal_months', null);
                      }}
                    />
                    <span className="text-sm">All time</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={permissions.temporal_access === 'recent_only'}
                      onChange={() => updatePermission('temporal_access', 'recent_only')}
                    />
                    <span className="text-sm">Recent only</span>
                  </label>
                  {permissions.temporal_access === 'recent_only' && (
                    <div className="ml-6 flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        max="120"
                        value={permissions.temporal_months || 6}
                        onChange={(e) => updatePermission('temporal_months', parseInt(e.target.value))}
                        className="w-20 px-2 py-1 border border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-600">months</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Preview Panel */}
              <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
                <h4 className="font-medium text-sm mb-2">Access Summary</h4>
                <p className="text-sm text-gray-700">{getAccessSummary()}</p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={handleReset}
                  disabled={saving}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reset to Role Defaults
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-16 text-gray-500">No permissions found</div>
          )}
        </div>
      </div>
    </div>
  );
};

// Display-only component for non-admin users
const PermissionsDisplay = ({ permissions, dataCategories }) => {
  const allowedCategories = dataCategories.filter(cat => permissions[cat.key]);
  
  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold mb-2">Client Scope</h3>
        <p className="text-sm text-gray-700">{permissions.client_scope?.replace('_', ' ')}</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold mb-2">Allowed Data Categories</h3>
        <ul className="text-sm text-gray-700 space-y-1">
          {allowedCategories.map(cat => (
            <li key={cat.key}>• {cat.label}</li>
          ))}
        </ul>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold mb-2">Sensitivity Level</h3>
        <p className="text-sm text-gray-700">{permissions.sensitivity_ceiling}</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold mb-2">AI Actions</h3>
        <p className="text-sm text-gray-700">{permissions.ai_action_level?.replace('_', ' ')}</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold mb-2">Time Range</h3>
        <p className="text-sm text-gray-700">
          {permissions.temporal_access === 'recent_only' 
            ? `Last ${permissions.temporal_months} months` 
            : 'All time'}
        </p>
      </div>
    </div>
  );
};

export default KnowledgeAccessSettings;
